import Button from "@/common/components/Button";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import Modal from "@/common/components/Modal";
import { BriefcaseBusinessIcon, FileEditIcon } from "lucide-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { CompanyGovernmentAllocationDistribution } from '@/features/governmentAllocations/types/governmentAllocationDistribution.type';
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema, getGovernmentAllocationDistributionSchema } from '@/features/governmentAllocations/schemas/createGovernmentAllocationDistribution.schema';
import useUpdateGovernmentAllocationDistribution from '@/features/governmentAllocations/hooks/useUpdateGovernmentAllocationDistribution';
import { updateGovernmentAllocationDistributionDefaultValues } from '@/features/governmentAllocations/utils/updateGovernmentAllocationDistributionDefaultValues';
import GovernmentAllocationDistributionProductCategoriesForm from '@/features/governmentAllocations/components/GovernmentAllocationDistribution/ProductCategoriesForm';
import { GovernmentAllocationTransformer } from "@/features/governmentAllocations/services/governmentAllocationsTransformer";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useParams } from "react-router-dom";
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from "@/features/governmentAllocations/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts";
import { GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationMovementsKind } from "@/features/governmentAllocations/types/governmentAllocation.type";
import { validateDistributionLimits } from "@/features/governmentAllocations/utils/validateDistributionLimits";

interface GovernmentAllocationDistributionUpdateModalProps {
  distribution: CompanyGovernmentAllocationDistribution;
  isConsumptionControlledByQuantity?: boolean;
  movementsKind?: GovernmentAllocationMovementsKind;
}

const GovernmentAllocationDistributionUpdateModal = ({
  distribution,
  isConsumptionControlledByQuantity,
  movementsKind = 'common',
}: GovernmentAllocationDistributionUpdateModalProps) => {
  const { id: governmentAllocationId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const schema = useMemo(
    () => getGovernmentAllocationDistributionSchema(isConsumptionControlledByQuantity),
    [isConsumptionControlledByQuantity],
  );

  const {
    data: allowedCategoriesAndProducts,
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId,
    filters: { companyBranchId: distribution?.branch?.id },
  });

  const {
    mutate: updateGovernmentAllocationDistribution,
    isPending: updateGovernmentAllocationDistributionIsPending,
  } = useUpdateGovernmentAllocationDistribution({
    distribution,
    isConsumptionControlledByQuantity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsDistributions'] });
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsAllowedCategoriesAndProducts'] });
      queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation'] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: GovernmentAllocationTransformer.errorsTransformer(),
      })(error);
    },
  });

  const formMethods = useForm<CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: updateGovernmentAllocationDistributionDefaultValues(distribution, isConsumptionControlledByQuantity, movementsKind),
  });

  const {
    register,
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { errors },
  } = formMethods;

  const onSubmit: SubmitHandler<
    | CreateGovernmentAllocationDistributionValidationSchema
    | CreateGovernmentAllocationDistributionQuantityValidationSchema
  > = (form) => {
    if (allowedCategoriesAndProducts) {
      const isValid = validateDistributionLimits({
        form,
        allowedCategoriesAndProducts,
        isConsumptionControlledByQuantity: !!isConsumptionControlledByQuantity,
        setError,
      });

      if (!isValid) return;
    }

    updateGovernmentAllocationDistribution(form);
  };

  const productCategoriesDistributions = watch('product_categories_distributions');

  useEffect(() => {
    if (isOpen) {
      reset(updateGovernmentAllocationDistributionDefaultValues(distribution, isConsumptionControlledByQuantity, movementsKind));
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value);

          if (!value) {
            setTimeout(reset, 500);
          }
        }}
        title="Detalhando crédito da filial"
        showClose
        className="min-w-[640px] overflow-hidden"
        Trigger={(
          <Button variant="link" operationKlassToCheckPermission='Client::Billing::GovernmentAllocationDistributions::Update'>
            <FileEditIcon strokeWidth={1} />
          </Button>
        )}
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={distribution?.branch?.general_information?.company_logo?.url}
                displayName={distribution?.branch?.display_name}
                imageClassName="size-8"
                iconClassName="size-6"
              />

              <div className="max-h-[480px] min-h-[320px] overflow-auto flex flex-col gap-6 pe-2">
                {isConsumptionControlledByQuantity ? (
                  <UnitTextField
                    label="Quantidade limite"
                    decimals={2}
                    autoCorrect="off"
                    forceValue
                    {...register('total_quantity')}
                    value={watch('total_quantity')}
                    errorMessage={(errors as { total_quantity?: { message?: string } }).total_quantity?.message || undefined}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                ) : (
                  <CurrencyTextField
                    label="Valor limite"
                    {...register('total_value')}
                    errorMessage={(errors as { total_value?: { message?: string } }).total_value?.message || undefined}
                    forceValue
                    value={watch('total_value')}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                )}

                {movementsKind !== 'fee' && (
                  <GovernmentAllocationDistributionProductCategoriesForm
                    distribution={distribution}
                    companyBranchId={distribution?.branch?.id}
                    isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
                  />
                )}
              </div>

              <Button onClick={handleSubmit(onSubmit)} disabled={updateGovernmentAllocationDistributionIsPending}>
                Salvar
              </Button>
            </div>
          </FormProvider>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default GovernmentAllocationDistributionUpdateModal;
